import { useEffect, useRef, useState } from 'react';

import { useFetchCallback } from '@services/services';

export interface UseHasExistingCatalogReturn {
  hasCatalog: boolean | null;
  isLoading: boolean;
  error: Error | null;
}

type ProductoListResponse = unknown[] | { count?: number };

const isObject = (val: unknown): val is Record<string, unknown> => typeof val === 'object' && val !== null;

const deriveHasCatalog = (response: ProductoListResponse): boolean => {
  if (Array.isArray(response)) return response.length > 0;
  if (isObject(response) && typeof response.count === 'number') return response.count > 0;
  return false;
};

export interface UseHasExistingCatalogOptions {
  /**
   * Si es `false`, el hook se monta pero NO dispara el GET y devuelve
   * `hasCatalog: null, isLoading: false`. Util para gatear la verificacion
   * cuando el caller esta detras de un feature flag y no necesita el dato.
   * Default `true` (comportamiento legacy).
   */
  enabled?: boolean;
}

/**
 * Detecta si el comercio activo ya tiene catálogo cargado.
 *
 * Hace UN solo GET a `/v1/producto?limit=1` y deriva el resultado tanto si el
 * backend responde con array (`length>0`) como si responde con `{count}`. Mientras
 * la red está en vuelo `hasCatalog` queda `null` para que el caller pueda
 * distinguir "todavía no sé" de "no tiene catálogo". Si el GET falla, se setea
 * `error` y `hasCatalog` queda `null` — el caller decide si muestra el step
 * igual o lo skippea.
 *
 * Cuando `options.enabled === false` el hook respeta las reglas de React Hooks
 * (siempre se invoca) pero salta el GET — evita network requests innecesarios
 * cuando el caller esta detras de un feature flag OFF.
 *
 * @param {UseHasExistingCatalogOptions} [options] - Configuracion opcional (`enabled` para gatear el GET).
 * @returns {UseHasExistingCatalogReturn} Estado del guard (`hasCatalog`, `isLoading`, `error`).
 */
export const useHasExistingCatalog = (options: UseHasExistingCatalogOptions = {}): UseHasExistingCatalogReturn => {
  const { enabled = true } = options;
  const { call } = useFetchCallback<{ params: { limit: number } }, ProductoListResponse>('get', '/v1/producto');
  const [hasCatalog, setHasCatalog] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<Error | null>(null);
  const didFetchRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      didFetchRef.current = false;
      setHasCatalog(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    let cancelled = false;

    const fetchCatalog = async (): Promise<void> => {
      try {
        const response = await call({ data: { params: { limit: 1 } } });
        if (cancelled) return;
        setHasCatalog(deriveHasCatalog(response));
      } catch (err) {
        if (cancelled) return;
        setHasCatalog(null);
        setError(err instanceof Error ? err : new Error('No se pudo verificar el catálogo existente'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCatalog().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [call, enabled]);

  return { hasCatalog, isLoading, error };
};
